import React from 'react';
import PropTypes from 'prop-types';

interface ProgressBarProps {
    percentage: number;
    yellowThreshold: number;
    redThreshold: number;
    isSimple?: boolean;
    isLight?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
}

/**
 * A bar that will be filled partway based on a percentage field.
 * 
 * ## _
 * ### Arguments
 * @param {number} percentage - The percentage amount that the progress bar should be. For example, pass in 50 for the progress to be at 50%.
 * @param {number} yellowThreshold - When `percentage` is more than this threshold, the progress bar will be yellow. Do not specify for no threshold.
 * @param {number} redThreshold - When `percentage` is more than this threshold, the progress bar will be red. Do not specify for no threshold.
 * @param {boolean} isSimple - A flag that removes the displayed percentage and makes the bar smaller.
 * @param {boolean} isLight - A flag that makes the bar white always.
 * @param {React.ReactNode} children - The contents inside the heading. This is usually just text.
 * @param {string} className - CSS classes to apply to the heading.
 */
class ProgressBar extends React.Component<ProgressBarProps> {
    static propTypes = {
        type: PropTypes.string,
        percentage: PropTypes.number,
        yellowThreshold: PropTypes.number,
        redThreshold: PropTypes.number,
        isSimple: PropTypes.bool,
        isLight: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
    }

    static defaultProps = {
        // Making these thresholds larger than the highest expected (100). If they go larger, that's the other dev's problem.
        yellowThreshold: 101,
        redThreshold: 101,
        className: "",
    }

    render() {
        const { percentage, yellowThreshold, redThreshold, isLight, isSimple, children, className, ...rest } = this.props;
        
        let classes = `m-progress-bar`

        if (isLight) classes += ` m-progress-bar--light`
        if (isSimple) classes += ` m-progress-bar--simple`
        
        if (className) classes += ` ${className}`

        return (
            <div className={classes}>
                <div className="m-progress-bar__inner" style={{flexBasis: "100%"}}>
                    <div className="m-progress-bar__progress-wrapper">
                        <p className="m-progress-bar__annotation">{percentage}%</p>
                        <div
                            className={`m-progress-bar__bar ${percentage >= redThreshold ? 'm-progress-bar__bar--red' : percentage >= yellowThreshold ? 'm-progress-bar__bar--yellow' : ''}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default ProgressBar;